// Search. One module with the two rankers the Swift app had, sharing the same helpers:
//  • rankItems — the recipe editor's ingredient/utensil picker (Swift RecipeItemSearchEngine)
//  • globalScore — the Search tab (Swift GlobalSearchScorer)
import { len, levenshtein, normalize, typoAllowance, uniq } from './text';

// ---------------------------------------------------------------------------
// Item picker: weighted fields, TF-IDF style rarity, singular/plural variants.

export type SearchField = { text: string; weight: number };

function singular(t: string): string {
  if (len(t) <= 3) return t;
  if (t.endsWith('ies')) return `${t.slice(0, -3)}y`;
  if (t.endsWith('es')) return t.slice(0, -2);
  if (t.endsWith('s')) return t.slice(0, -1);
  return t;
}

function plural(t: string): string {
  if (t.endsWith('y')) return `${t.slice(0, -1)}ies`;
  if (t.endsWith('s')) return t;
  return `${t}s`;
}

/** Tokens with singular/plural variants, ≥2 characters, de-duplicated (both Swift tokenisers agree). */
export function variantTokens(text: string): string[] {
  const out: string[] = [];
  for (const raw of normalize(text).split(' ').filter(Boolean)) {
    for (const v of uniq([raw, singular(raw), plural(raw)])) if (len(v) >= 2) out.push(v);
  }
  return uniq(out);
}

function tokenScore(q: string, f: string): number {
  if (q === f) return 1;
  if (f.startsWith(q) || q.startsWith(f)) return 0.92;
  if (f.includes(q) || q.includes(f)) return 0.78;
  const allowance = typoAllowance(Math.max(len(q), len(f)));
  if (allowance <= 0) return 0;
  const d = levenshtein(q, f, allowance);
  if (d > allowance) return 0;
  return Math.max(0.62, 0.88 - d * 0.12);
}

export const INGREDIENT_FIELD_WEIGHTS = { title: 3.0, synonyms: 2.4, aisle: 0.7, explainer: 0.4 } as const;

/**
 * Ranks items for the picker. `fields(item)` returns weighted text fields; `title(item)`
 * breaks ties alphabetically. Returns at most `limit` items with score > 0.
 */
export function rankItems<T>(
  query: string,
  items: T[],
  fields: (item: T) => SearchField[],
  title: (item: T) => string,
  limit = 5,
): { item: T; score: number }[] {
  const queryTokens = variantTokens(query);
  if (queryTokens.length === 0 || limit <= 0) return [];
  const indexed = items.map(item => ({
    item,
    fields: fields(item).filter(f => f.text !== '').map(f => ({ weight: f.weight, tokens: variantTokens(f.text) })),
  }));
  const df = new Map<string, number>();
  for (const it of indexed) for (const t of new Set(it.fields.flatMap(f => f.tokens))) df.set(t, (df.get(t) ?? 0) + 1);
  const n = Math.max(indexed.length, 1);

  const scored: { item: T; score: number }[] = [];
  for (const it of indexed) {
    let total = 0;
    let matched = 0;
    for (const q of queryTokens) {
      const idf = Math.log((n + 1) / ((df.get(q) ?? 0) + 1)) + 1;
      let best = 0;
      for (const f of it.fields) {
        let bestToken = 0;
        for (const t of f.tokens) bestToken = Math.max(bestToken, tokenScore(q, t));
        best = Math.max(best, f.weight * bestToken);
      }
      if (best > 0) {
        matched += 1;
        total += best * idf;
      }
    }
    const coverage = matched / queryTokens.length;
    if (coverage > 0 && total > 0) scored.push({ item: it.item, score: total * (0.45 + coverage) });
  }
  return scored
    .sort((a, b) => b.score - a.score || title(a.item).localeCompare(title(b.item), undefined, { sensitivity: 'accent' }))
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Search tab: best weighted field match, results need ≥ 0.6.

export const GLOBAL_MIN_SCORE = 0.6;

function typoMatches(a: string, b: string): boolean {
  const allowance = typoAllowance(Math.max(len(a), len(b)));
  if (allowance <= 0) return a === b;
  return levenshtein(a, b, allowance) <= allowance;
}

function fieldScore(query: string, term: string): number {
  if (!term) return 0;
  if (query === term) return 1;
  if (term.startsWith(query)) return 0.96;
  if (term.includes(` ${query} `) || term.endsWith(` ${query}`)) return 0.9;
  const qt = query.split(' ');
  const tt = term.split(' ');
  if (qt.every(q => tt.some(t => t.startsWith(q) || typoMatches(q, t)))) return qt.length === tt.length ? 0.88 : 0.76;
  let best = 0;
  for (const t of tt) {
    const s = t.startsWith(query) || query.startsWith(t) ? 0.7 : typoMatches(query, t) ? 0.65 : 0;
    best = Math.max(best, s);
  }
  return best;
}

/** Title ×1.0, explainer ×0.82, extra terms (ingredients, synonyms, step titles) ×0.72. */
export function globalScore(query: string, title: string, explainer: string, terms: string[]): number {
  const q = normalize(query);
  if (len(q) < 2) return 0;
  const weighted: [string, number][] = [[title, 1], [explainer, 0.82], ...terms.map(t => [t, 0.72] as [string, number])];
  let best = 0;
  for (const [text, weight] of weighted) best = Math.max(best, fieldScore(q, normalize(text)) * weight);
  return best;
}
